export const MARKET_TERMS = [
    // ------------------------------------------------------------------ market
    {
        id: 'admitted',
        term: 'Admitted carrier',
        aka: ['licensed carrier', 'standard market'],
        category: 'market',
        essential: true,
        def: 'A carrier licensed by the state to write business there. Its rates and forms are filed with and approved by the state, and its policyholders are protected by the state guaranty fund if it fails.',
        detail: 'The trade-off is flexibility: filed rates and forms mean an admitted carrier generally cannot tailor terms or price to an unusual risk. That is the whole reason the surplus lines market exists.',
        confused: 'Admitted is about licensing, not financial strength. Plenty of non-admitted carriers are A-rated and larger than admitted ones.',
        see: ['non-admitted', 'surplus-lines', 'guaranty-fund', 'filed-rates'],
    },
    {
        id: 'non-admitted',
        term: 'Non-admitted carrier',
        aka: ['unlicensed', 'surplus lines carrier'],
        category: 'market',
        essential: true,
        def: 'A carrier not licensed in the state, permitted to write there only through the surplus lines process. Its forms and rates are not filed or approved.',
        detail: 'No guaranty fund protection if it becomes insolvent — the insured has no backstop. That is the single most important thing to disclose, and most states require a written notice on the policy saying so.',
        see: ['admitted', 'surplus-lines', 'diligent-effort', 'guaranty-fund', 'surplus-lines-tax'],
    },
    {
        id: 'surplus-lines',
        term: 'Surplus lines',
        aka: ['e&s', 'excess and surplus', 'non-standard'],
        category: 'market',
        essential: true,
        def: 'The market for risks the admitted market will not write — unusual, hazardous, high-limit, poor loss history, or simply new. Written by non-admitted carriers under a special state process.',
        detail: 'Placing it requires a surplus lines licence, a diligent effort search in most states, a signed disclosure to the insured, and payment of surplus lines tax and stamping fees. The carrier can write any form and any price, which is the point.',
        see: ['non-admitted', 'diligent-effort', 'export-list', 'wholesale-broker', 'surplus-lines-tax'],
    },
    {
        id: 'diligent-effort',
        term: 'Diligent effort',
        aka: ['diligent search', 'declinations', 'affidavit'],
        category: 'regulatory',
        def: 'The requirement to try the admitted market first before placing surplus lines — usually documented with a set number of declinations from licensed carriers.',
        detail: 'Three declinations is the common standard, and the affidavit is filed with the state or stamping office. Risks on the export list are exempt. Skipping it is a licensing violation, not a paperwork slip.',
        see: ['surplus-lines', 'export-list', 'stamping-office', 'declination'],
    },
    {
        id: 'export-list',
        term: 'Export list',
        aka: ['white list'],
        category: 'regulatory',
        def: 'A state list of risk types that may go straight to surplus lines without a diligent search, because the admitted market is known not to write them.',
        see: ['diligent-effort', 'surplus-lines'],
    },
    {
        id: 'stamping-office',
        term: 'Stamping office',
        aka: ['surplus lines association'],
        category: 'regulatory',
        def: 'A state body that reviews surplus lines filings for compliance and charges a small fee — the stamping fee — on each policy.',
        see: ['surplus-lines-tax', 'diligent-effort', 'surplus-lines'],
    },
    {
        id: 'surplus-lines-tax',
        term: 'Surplus lines tax',
        category: 'money',
        def: 'A state premium tax on non-admitted business, typically 2–6%, charged to the insured on top of premium and remitted by whoever holds the surplus lines licence.',
        detail: 'Taxes and stamping fees are almost never commissionable, and they are calculated on premium plus certain fees depending on the state — which is why the invoice total rarely matches the quoted premium.',
        see: ['stamping-office', 'taxes-and-fees', 'surplus-lines'],
    },
    {
        id: 'guaranty-fund',
        term: 'Guaranty fund',
        aka: ['guaranty association'],
        category: 'regulatory',
        def: 'A state-run backstop that pays claims when an admitted carrier becomes insolvent, up to statutory caps.',
        detail: 'Surplus lines business is not covered. Neither, generally, are very large commercial insureds in some states. It is the concrete reason "non-admitted" matters to the insured rather than just to the agent.',
        see: ['admitted', 'non-admitted', 'insolvency'],
    },
    {
        id: 'wholesale-broker',
        term: 'Wholesale broker',
        aka: ['wholesaler', 'surplus lines broker'],
        category: 'market',
        essential: true,
        def: 'A broker who sits between the retail agent and carriers the retail agent cannot access directly — usually surplus lines markets.',
        detail: 'The wholesaler holds the surplus lines licence and handles the filings, and takes a share of the commission. They do not have a relationship with the insured and generally do not want one.',
        confused: 'A wholesaler brokers to markets. An MGA holds underwriting authority and can bind on a carrier\'s behalf. The same firm often does both.',
        see: ['retail-agent', 'mga', 'surplus-lines', 'binding-authority'],
    },
    {
        id: 'retail-agent',
        term: 'Retail agent',
        aka: ['producer', 'retail broker'],
        category: 'market',
        def: 'The agent with the direct relationship with the insured — the one who advises them and holds the account.',
        see: ['wholesale-broker', 'producer', 'agent-of-record'],
    },
    {
        id: 'mga',
        term: 'Managing general agent',
        short: 'MGA',
        category: 'market',
        essential: true,
        def: 'A firm a carrier has given authority to underwrite, quote, bind and often issue policies on its behalf, within agreed limits.',
        detail: 'When you deal with an MGA you are effectively dealing with the carrier for that programme. What they cannot do is written in their binding authority agreement, which is why some things need to be "referred to the company".',
        see: ['mgu', 'binding-authority', 'program-business', 'wholesale-broker'],
    },
    {
        id: 'mgu',
        term: 'Managing general underwriter',
        short: 'MGU',
        category: 'market',
        def: 'Much the same as an MGA, with the emphasis on underwriting authority rather than agency functions. The terms are used loosely and often interchangeably.',
        see: ['mga', 'binding-authority'],
    },
    {
        id: 'binding-authority',
        term: 'Binding authority',
        aka: ['binder authority'],
        category: 'market',
        def: 'Written permission from a carrier to put coverage in force on its behalf, within defined classes, limits and territories.',
        detail: 'Exceeding it does not necessarily void coverage for an innocent insured — apparent authority may still bind the carrier — but it is a serious problem for the agency relationship and for E&O.',
        see: ['mga', 'apparent-authority', 'binder'],
    },
    {
        id: 'program-business',
        term: 'Program business',
        category: 'market',
        def: 'A packaged insurance offering built for one industry — pest control, breweries, physical therapists — with tailored forms, pre-set pricing and a single point of entry.',
        detail: 'Programmes are usually run by an MGA on one carrier\'s paper. They quote quickly and know the class, but they are rigid: an account slightly outside the box gets declined rather than negotiated.',
        see: ['mga', 'appetite'],
    },
    {
        id: 'captive',
        term: 'Captive insurer',
        category: 'market',
        def: 'An insurance company owned by the businesses it insures, formed to keep risk and premium in-house rather than paying it to the open market.',
        detail: 'Single-parent captives serve one company; group captives pool several similar mid-sized businesses. They suit accounts with good losses, stable cash flow and a long horizon — and they are a hard place to leave once entered.',
        see: ['fronting', 'rrg', 'self-insurance'],
    },
    {
        id: 'rrg',
        term: 'Risk retention group',
        short: 'RRG',
        category: 'market',
        def: 'A liability-only insurer owned by its members, all from the same industry, licensed in one state and able to write in every other under federal law.',
        detail: 'Not covered by guaranty funds, and it can only write liability — never property or workers\' comp. Common in trucking, healthcare and professional groups.',
        see: ['captive', 'guaranty-fund', 'reciprocal'],
    },
    {
        id: 'reciprocal',
        term: 'Reciprocal exchange',
        category: 'market',
        def: 'An insurer owned by its policyholders, who insure each other, run by an attorney-in-fact.',
        see: ['mutual-insurer', 'stock-insurer'],
    },
    {
        id: 'mutual-insurer',
        term: 'Mutual insurer',
        category: 'market',
        def: 'A carrier owned by its policyholders rather than shareholders. Profits return to policyholders as dividends or lower rates.',
        see: ['stock-insurer', 'reciprocal', 'dividend'],
    },
    {
        id: 'stock-insurer',
        term: 'Stock insurer',
        category: 'market',
        def: 'A carrier owned by shareholders, run for their return.',
        see: ['mutual-insurer'],
    },
    {
        id: 'lloyds',
        term: "Lloyd's of London",
        category: 'market',
        def: 'A marketplace, not an insurance company. Syndicates of capital providers underwrite risks there, mostly surplus lines in the US.',
        detail: 'A Lloyd\'s policy is backed by the Central Fund and Lloyd\'s holds an A.M. Best rating for the market as a whole, which is why "Lloyd\'s syndicate" on a quote is generally acceptable security despite being non-admitted.',
        see: ['syndicate', 'non-admitted', 'am-best'],
    },
    {
        id: 'syndicate',
        term: 'Syndicate',
        category: 'market',
        def: 'A group of capital providers at Lloyd\'s that underwrites risks as a unit, identified by number.',
        see: ['lloyds'],
    },
    {
        id: 'reinsurance',
        term: 'Reinsurance',
        category: 'market',
        def: 'Insurance bought by insurers. It lets a carrier write bigger limits than its own balance sheet would allow and smooths out catastrophe years.',
        detail: 'Reinsurance cost is a major driver of the market cycle — when reinsurers raise prices after a bad catastrophe season, primary rates follow within a year or two.',
        see: ['treaty-reinsurance', 'facultative', 'hard-market'],
    },
    {
        id: 'treaty-reinsurance',
        term: 'Treaty reinsurance',
        category: 'market',
        def: 'An agreement covering a whole class of a carrier\'s business automatically, rather than risk by risk.',
        see: ['reinsurance', 'facultative'],
    },
    {
        id: 'facultative',
        term: 'Facultative reinsurance',
        aka: ['fac'],
        category: 'market',
        def: 'Reinsurance placed one risk at a time, usually because the risk is too large or unusual for the treaty.',
        detail: 'When an underwriter says they need to "get fac", expect a slower answer — they are shopping your risk to a reinsurer before they can quote it.',
        see: ['treaty-reinsurance', 'reinsurance'],
    },
    {
        id: 'fronting',
        term: 'Fronting',
        category: 'market',
        def: 'An admitted carrier issuing a policy and then passing nearly all the risk to a captive or reinsurer, so the insured gets admitted paper without the carrier taking the risk.',
        detail: 'Used where admitted paper is required — certificates for contracts, statutory workers\' comp, motor carrier filings — but the economics sit in a captive. The fronting carrier charges a fee and requires collateral.',
        see: ['captive', 'admitted', 'collateral'],
    },
    {
        id: 'paper',
        term: 'Paper',
        category: 'market',
        def: 'Whose policy it actually is. "A-rated paper", "admitted paper", "whose paper is that on" — meaning which insurance company carries the risk.',
        see: ['admitted', 'fronting', 'am-best'],
    },
    {
        id: 'am-best',
        term: 'A.M. Best rating',
        category: 'market',
        essential: true,
        def: 'A financial strength rating for insurers, from A++ down to D. Most commercial contracts and lenders require A- or better.',
        detail: 'Two parts matter: the letter and the financial size category (FSC), which measures surplus. A contract requiring "A- VII or better" is asking for both.',
        see: ['paper', 'admitted', 'insolvency'],
    },
    {
        id: 'insolvency',
        term: 'Insolvency',
        category: 'market',
        def: 'A carrier failing financially and being placed in liquidation by its regulator. Admitted business moves to the state guaranty fund; surplus lines business does not.',
        see: ['guaranty-fund', 'am-best', 'non-admitted'],
    },
    {
        id: 'hard-market',
        term: 'Hard market',
        category: 'market',
        essential: true,
        def: 'A period when carriers restrict appetite and raise rates — capacity is scarce, underwriting is strict, and renewals come back with increases and reduced terms.',
        detail: 'Hard markets do not arrive evenly. A class or a geography can be hard while everything else is soft, which is why "the market is hard" is never a sufficient explanation to an insured.',
        confused: 'A soft market is the opposite: plenty of capacity, competition on price, broad terms and easy underwriting.',
        see: ['soft-market', 'market-cycle', 'capacity', 'reinsurance'],
    },
    {
        id: 'soft-market',
        term: 'Soft market',
        category: 'market',
        def: 'A period of abundant capacity, falling rates, broad coverage and relaxed underwriting.',
        see: ['hard-market', 'market-cycle'],
    },
    {
        id: 'market-cycle',
        term: 'Market cycle',
        category: 'market',
        def: 'The swing between hard and soft markets, driven by carrier results, reinsurance cost, investment returns and catastrophe losses.',
        see: ['hard-market', 'soft-market'],
    },
    {
        id: 'capacity',
        term: 'Capacity',
        category: 'market',
        def: 'How much limit a carrier or the market as a whole is willing to put up. Also used for the maximum a single carrier will write on one risk.',
        see: ['hard-market', 'reinsurance', 'excess-liability'],
    },
    {
        id: 'appetite',
        term: 'Appetite',
        category: 'underwriting',
        essential: true,
        def: 'What a carrier actually wants to write — the classes, sizes, states and characteristics it is looking for.',
        detail: 'Appetite is narrower than the classes a carrier is licensed for, and it moves. An appetite guide tells you where not to waste a submission, which is worth more than knowing where to send one.',
        see: ['class-code', 'declination', 'submission', 'program-business'],
    },
    // ------------------------------------------------------------- underwriting
    {
        id: 'underwriter',
        term: 'Underwriter',
        category: 'underwriting',
        essential: true,
        def: 'The person at a carrier who decides whether to write a risk, on what terms, and at what price.',
        detail: 'Underwriters have authority limits like anyone else. Knowing whether the one you are talking to can actually say yes — or must refer it — is the difference between a two-day answer and a two-week one.',
        see: ['appetite', 'binding-authority', 'referral'],
    },
    {
        id: 'submission',
        term: 'Submission',
        category: 'practice',
        essential: true,
        def: 'The package sent to a carrier asking them to quote — application, loss runs, supplementals, and whatever else explains the risk.',
        detail: 'A complete submission gets quoted; an incomplete one goes to the bottom of the pile. In a hard market underwriters triage on completeness before they read the risk.',
        see: ['application', 'loss-run', 'supplemental-application', 'marketing-an-account'],
    },
    {
        id: 'declination',
        term: 'Declination',
        aka: ['decline'],
        category: 'underwriting',
        def: 'A carrier refusing to quote. In surplus lines, documented declinations are what satisfies the diligent effort requirement.',
        detail: 'Get them in writing. An underwriter\'s verbal "we would not be interested" is not a declination when the state asks for the affidavit.',
        see: ['diligent-effort', 'submission', 'appetite'],
    },
    {
        id: 'referral',
        term: 'Referral',
        category: 'underwriting',
        def: 'A risk that exceeds an underwriter\'s or an MGA\'s authority and has to go up for approval.',
        see: ['underwriter', 'binding-authority'],
    },
    {
        id: 'class-code',
        term: 'Class code',
        category: 'underwriting',
        essential: true,
        def: 'The number identifying what a business does, which drives rating. General liability uses five-digit ISO codes; workers\' comp uses four-digit NCCI codes.',
        detail: 'Classing a business wrongly is the most common rating error and cuts both ways: it can produce a premium the audit will reverse, or a coverage argument when the carrier says it never intended to write those operations.',
        see: ['naics', 'sic', 'ncci', 'iso', 'audit'],
    },
    {
        id: 'naics',
        term: 'NAICS code',
        category: 'underwriting',
        def: 'The North American Industry Classification System — the six-digit federal code for an industry. Used by carriers for appetite and analytics.',
        confused: 'NAICS replaced SIC for government use, but plenty of insurance systems still ask for SIC. They are not the same numbers.',
        see: ['sic', 'class-code'],
    },
    {
        id: 'sic',
        term: 'SIC code',
        category: 'underwriting',
        def: 'Standard Industrial Classification — the older four-digit industry code. Still requested by many carriers and AMS systems.',
        see: ['naics', 'class-code'],
    },
    {
        id: 'iso',
        term: 'ISO',
        aka: ['insurance services office', 'verisk'],
        category: 'underwriting',
        def: 'The organisation that writes the standard policy forms and files advisory rating information most carriers build on.',
        detail: 'When a form number starts CG, CA, CP or IM, it is an ISO form. Carriers may use ISO forms unchanged, amend them by endorsement, or write their own — and "proprietary form" is a flag to read rather than assume.',
        see: ['coverage-form', 'class-code', 'protection-class'],
    },
    {
        id: 'rating-basis',
        term: 'Rating basis',
        aka: ['exposure basis', 'premium basis'],
        category: 'underwriting',
        essential: true,
        def: 'The measure premium is calculated on — payroll, gross sales, square footage, number of units, vehicle count, admissions.',
        detail: 'It differs by class within the same policy: a contractor might be rated on payroll for one code and receipts for another. Estimating it low does not save money; it defers the cost to the audit.',
        see: ['rate', 'audit', 'class-code', 'exposure'],
    },
    {
        id: 'rate',
        term: 'Rate',
        category: 'underwriting',
        def: 'The price per unit of exposure — per $1,000 of payroll, per $100 of receipts, per vehicle.',
        confused: 'Rate is the price per unit. Premium is rate times exposure. A flat renewal premium with growing sales is actually a rate decrease.',
        see: ['rating-basis', 'premium', 'loss-cost'],
    },
    {
        id: 'loss-cost',
        term: 'Loss cost',
        category: 'underwriting',
        def: 'The pure expected claims portion of a rate, before the carrier adds expenses and profit. Advisory loss costs are published by ISO and NCCI.',
        detail: 'Carriers file a loss cost multiplier — the factor they apply to the advisory loss cost. Comparing multipliers is how you compare two carriers\' base pricing on the same class.',
        see: ['rate', 'iso', 'ncci'],
    },
    {
        id: 'loss-ratio',
        term: 'Loss ratio',
        category: 'underwriting',
        essential: true,
        def: 'Losses divided by premium. A 60% loss ratio means the carrier paid out sixty cents of every premium dollar in claims.',
        detail: 'Carriers generally need loss ratios in the fifties or low sixties to make money once expenses are added. On an account, it is the number an underwriter forms their opinion from — and the one to calculate yourself before you market it.',
        see: ['combined-ratio', 'loss-run', 'experience-rating'],
    },
    {
        id: 'combined-ratio',
        term: 'Combined ratio',
        category: 'underwriting',
        def: 'Losses plus expenses divided by premium. Above 100% means the carrier lost money on underwriting, before investment income.',
        see: ['loss-ratio'],
    },
    {
        id: 'experience-rating',
        term: 'Experience rating',
        category: 'underwriting',
        def: 'Adjusting premium based on the insured\'s own past losses compared with what is expected for their class and size.',
        detail: 'Mandatory and formula-driven in workers\' comp via the experience modification factor. In liability and property it is more discretionary and blends into schedule rating.',
        see: ['experience-mod', 'schedule-rating', 'loss-run'],
    },
    {
        id: 'schedule-rating',
        term: 'Schedule rating',
        aka: ['irpm', 'credits and debits'],
        category: 'underwriting',
        def: 'Underwriter discretion to raise or lower premium — commonly up to ±25% or ±40% — for characteristics the class rate does not capture: management, training, premises condition, employee turnover.',
        detail: 'This is the negotiable part of the price, and it must be justified in the file. Giving an underwriter documented reasons — a safety programme, a new fleet policy, driver training — is how you actually earn credits rather than ask for them.',
        see: ['experience-rating', 'irpm', 'loss-control'],
    },
    {
        id: 'irpm',
        term: 'Individual risk premium modification',
        short: 'IRPM',
        category: 'underwriting',
        def: 'The formal name for schedule rating credits and debits.',
        see: ['schedule-rating'],
    },
    {
        id: 'retrospective-rating',
        term: 'Retrospective rating',
        aka: ['retro', 'retro plan'],
        category: 'underwriting',
        def: 'A plan where final premium is calculated after the policy period based on actual losses, between a minimum and a maximum.',
        detail: 'Suits large accounts with good loss control and cash to fund the swing. The insured is effectively self-insuring the working layer, and adjustments continue for years as claims develop.',
        see: ['loss-sensitive', 'collateral', 'ibnr'],
    },
    {
        id: 'loss-sensitive',
        term: 'Loss sensitive program',
        category: 'underwriting',
        def: 'Any structure where the insured\'s final cost moves with their own losses — retro plans, large deductibles, captives.',
        see: ['retrospective-rating', 'captive', 'collateral'],
    },
    {
        id: 'collateral',
        term: 'Collateral',
        category: 'money',
        def: 'Security a carrier holds — a letter of credit, cash, a trust — against losses the insured will reimburse under a deductible or retro plan.',
        detail: 'It ties up the insured\'s credit line and does not go away when the programme ends: collateral is held until the claims are closed, which can be years.',
        see: ['loss-sensitive', 'retrospective-rating', 'fronting'],
    },
    {
        id: 'minimum-premium',
        term: 'Minimum premium',
        category: 'money',
        def: 'The least a carrier will charge for a policy, however small the exposure.',
        see: ['deposit-premium', 'minimum-earned-premium'],
    },
    {
        id: 'deposit-premium',
        term: 'Deposit premium',
        aka: ['estimated premium'],
        category: 'money',
        def: 'The amount charged at inception on an auditable policy, based on estimated exposures. The audit trues it up.',
        see: ['audit', 'rating-basis', 'minimum-premium'],
    },
    {
        id: 'audit',
        term: 'Premium audit',
        category: 'money',
        essential: true,
        def: 'A review after the policy period comparing estimated exposures with what actually happened, producing additional premium or a return.',
        detail: 'Audits are where relationships break. An insured who lowballed payroll to get a cheaper quote gets a bill twelve months later, and often blames the agent. Setting estimates honestly at inception is the fix; so is warning them mid-term when sales run ahead of plan.',
        see: ['rating-basis', 'deposit-premium', 'audit-noncompliance'],
    },
    {
        id: 'audit-noncompliance',
        term: 'Audit noncompliance charge',
        category: 'money',
        def: 'A penalty premium — often two or three times the deposit — charged when an insured refuses or fails to cooperate with the audit.',
        detail: 'It sticks, and it follows the insured to their next carrier as an unpaid balance. Chase the audit paperwork rather than waiting for the invoice.',
        see: ['audit'],
    },
    {
        id: 'inspection',
        term: 'Inspection',
        aka: ['loss control survey', 'risk control visit'],
        category: 'underwriting',
        def: 'A carrier visit to see the risk in person — premises condition, housekeeping, safety practices, equipment.',
        detail: 'Inspections generate recommendations, and unaddressed recommendations are a common reason for mid-term cancellation or non-renewal. Diary them the day the report arrives.',
        see: ['loss-control', 'subjectivities', 'recommendations'],
    },
    {
        id: 'loss-control',
        term: 'Loss control',
        aka: ['risk control', 'safety services'],
        category: 'underwriting',
        def: 'Carrier services aimed at preventing losses — surveys, training, safety programme help, ergonomics, fleet management.',
        detail: 'It is a real differentiator on comp and fleet accounts, and a fair thing to compare between quotes when the prices are close.',
        see: ['inspection', 'schedule-rating', 'recommendations'],
    },
    {
        id: 'recommendations',
        term: 'Recommendations',
        aka: ['recs'],
        category: 'underwriting',
        def: 'Items a carrier requires or requests after an inspection — a handrail, an updated panel, a written safety programme — usually with a deadline.',
        see: ['inspection', 'loss-control', 'cancellation'],
    },
    {
        id: 'cope',
        term: 'COPE',
        category: 'property',
        def: 'The four things a property underwriter assesses: Construction, Occupancy, Protection, Exposure.',
        detail: 'Construction is what the building is made of, occupancy is what happens inside, protection is sprinklers and fire service, exposure is what is next door. Getting all four right in a submission is most of property underwriting.',
        see: ['construction-class', 'protection-class', 'tiv'],
    },
    {
        id: 'construction-class',
        term: 'Construction class',
        aka: ['iso construction class'],
        category: 'property',
        def: 'How a building is built, from frame (class 1) through joisted masonry, non-combustible, masonry non-combustible, modified fire resistive, to fire resistive (class 6).',
        detail: 'It drives rate more than almost anything else on a property risk, and it is frequently reported wrong — a masonry veneer on a wood frame is frame, not joisted masonry.',
        see: ['cope', 'protection-class'],
    },
    {
        id: 'protection-class',
        term: 'Protection class',
        aka: ['ppc', 'fire protection class'],
        category: 'property',
        def: 'An ISO rating from 1 to 10 for how good the local fire protection is — the fire department, the water supply, and how far away they are.',
        detail: 'Class 10 means effectively unprotected and prices accordingly. The split classes (e.g. 4/4Y) turn on distance to a hydrant and to the fire station.',
        see: ['cope', 'iso'],
    },
    {
        id: 'tiv',
        term: 'Total insured value',
        short: 'TIV',
        category: 'property',
        def: 'The sum of all values insured at a location or across a schedule — building, contents and business income.',
        see: ['statement-of-values', 'coinsurance', 'pml'],
    },
    {
        id: 'pml',
        term: 'Probable maximum loss',
        short: 'PML',
        aka: ['mfl', 'maximum foreseeable loss'],
        category: 'underwriting',
        def: 'The worst loss a carrier realistically expects at a location, given the protections in place. Used to decide how much capacity to commit.',
        see: ['tiv', 'capacity', 'catastrophe-model'],
    },
    {
        id: 'catastrophe-model',
        term: 'Catastrophe model',
        aka: ['cat model'],
        category: 'underwriting',
        def: 'Software that estimates losses from hurricane, earthquake, wildfire and flood for a specific address, driving both price and whether a carrier will write it at all.',
        detail: 'Model output is address-specific, so a hundred feet can change the answer. It is also why a clean loss history does not save a coastal risk from a rate increase.',
        see: ['pml', 'named-storm', 'capacity'],
    },
    {
        id: 'named-storm',
        term: 'Named storm',
        category: 'property',
        def: 'A storm given a name by the National Hurricane Center. Coastal policies apply a separate, usually percentage, deductible to losses from one.',
        detail: 'The trigger window matters: coverage language usually runs from the time a watch or warning is issued until 72 hours after it ends, so a loss the day before naming falls under the ordinary wind deductible.',
        see: ['percentage-deductible', 'catastrophe-model', 'flood'],
    },
    // -------------------------------------------------------------------- money
    {
        id: 'commission',
        term: 'Commission',
        category: 'money',
        essential: true,
        def: 'The agency\'s share of premium, paid by the carrier as a percentage. Commercial lines commonly runs 10–20% depending on line and carrier.',
        detail: 'Not everything is commissionable: taxes, stamping fees, policy fees and often the surplus lines broker\'s fee are not. Wholesale placements split commission between the retailer and the wholesaler.',
        see: ['contingent-commission', 'fee-in-lieu', 'taxes-and-fees'],
    },
    {
        id: 'contingent-commission',
        term: 'Contingent commission',
        aka: ['profit sharing', 'bonus commission'],
        category: 'money',
        def: 'Extra compensation paid to an agency based on the volume, growth or profitability of the business it placed with a carrier.',
        detail: 'Paid after the fact and dependent on loss ratio, so it is not guaranteed income. Some states require disclosure to commercial clients on request.',
        see: ['commission', 'loss-ratio'],
    },
    {
        id: 'fee-in-lieu',
        term: 'Fee in lieu of commission',
        category: 'money',
        def: 'Charging the client a flat fee for the agency\'s services instead of taking carrier commission — common on large accounts.',
        detail: 'Rules differ by state, and combining a fee with commission on the same policy is restricted or prohibited in several. Get it in a signed service agreement.',
        see: ['commission', 'broker-fee'],
    },
    {
        id: 'broker-fee',
        term: 'Broker fee',
        aka: ['agency fee', 'policy fee'],
        category: 'money',
        def: 'A fee charged on top of premium — by the retail agency, the wholesaler, or the carrier — for placing or issuing the policy.',
        detail: 'State rules govern who may charge one, how it must be disclosed, and whether it is refundable on cancellation. Wholesaler and carrier policy fees are usually fully earned at inception.',
        see: ['taxes-and-fees', 'fee-in-lieu', 'minimum-earned-premium'],
    },
    {
        id: 'taxes-and-fees',
        term: 'Taxes and fees',
        category: 'money',
        def: 'Everything added to premium on the invoice — surplus lines tax, stamping fee, policy fee, inspection fee, broker fee, state assessments.',
        detail: 'On a surplus lines placement these can add 5–10% to the quoted premium. Show them in the proposal; an insured who finds them on the invoice reads it as a bait and switch.',
        see: ['surplus-lines-tax', 'broker-fee', 'premium'],
    },
    {
        id: 'earned-premium',
        term: 'Earned premium',
        category: 'money',
        def: 'The portion of premium covering time that has already passed. The rest is unearned and would be returned if the policy cancelled.',
        see: ['unearned-premium', 'pro-rata', 'minimum-earned-premium'],
    },
    {
        id: 'unearned-premium',
        term: 'Unearned premium',
        category: 'money',
        def: 'Premium for coverage not yet provided — what a pro rata cancellation returns.',
        see: ['earned-premium', 'return-premium', 'pro-rata'],
    },
    {
        id: 'minimum-earned-premium',
        term: 'Minimum earned premium',
        short: 'MEP',
        category: 'money',
        essential: true,
        def: 'A percentage of premium the carrier keeps no matter when the policy cancels — commonly 25% on surplus lines, and 100% on some catastrophe-exposed property and builders risk.',
        detail: 'This is the single most common billing surprise on E&S business. An insured who cancels a 25% MEP policy after one month gets far less back than they expect. It belongs in the proposal, not only in the policy.',
        see: ['earned-premium', 'flat-cancellation', 'return-premium', 'surplus-lines'],
    },
    {
        id: 'return-premium',
        term: 'Return premium',
        category: 'money',
        def: 'Money owed back to the insured after a cancellation, a reduction in coverage, or an audit that shows lower exposures than estimated.',
        see: ['unearned-premium', 'pro-rata', 'audit', 'first-named-insured'],
    },
    {
        id: 'direct-bill',
        term: 'Direct bill',
        category: 'money',
        def: 'The carrier invoices the insured and pays the agency its commission. Most small and mid-market commercial business works this way.',
        confused: 'Agency bill means the agency invoices the insured, collects the money, keeps the commission and remits the rest — and carries the credit risk.',
        see: ['agency-bill', 'trust-account'],
    },
    {
        id: 'agency-bill',
        term: 'Agency bill',
        category: 'money',
        def: 'The agency invoices and collects premium from the insured, then remits it to the carrier net of commission.',
        detail: 'The agency is responsible to the carrier for the premium whether or not the insured pays, which is why aged agency-bill receivables are a real risk rather than a bookkeeping annoyance.',
        see: ['direct-bill', 'trust-account', 'aging'],
    },
    {
        id: 'trust-account',
        term: 'Premium trust account',
        aka: ['fiduciary account'],
        category: 'regulatory',
        def: 'A separate bank account holding premium collected from insureds and owed to carriers. Most states require it and prohibit using the money for agency operations.',
        detail: 'Commingling premium with operating funds is one of the fastest ways to lose a licence. It is a fiduciary obligation, not an accounting preference.',
        see: ['agency-bill', 'aging'],
    },
    {
        id: 'aging',
        term: 'Aging',
        aka: ['receivables aging'],
        category: 'money',
        def: 'How long invoices have been outstanding, bucketed by 30, 60, 90 days.',
        see: ['agency-bill', 'trust-account'],
    },
    {
        id: 'premium-finance',
        term: 'Premium finance',
        category: 'money',
        def: 'A third-party lender pays the premium up front and the insured repays in instalments, with interest, secured by the right to cancel the policy.',
        detail: 'The finance company holds a power of attorney to cancel for non-payment, and it will use it. Watch the down payment against the minimum earned premium — if MEP exceeds what the finance company can recover, the insured owes the difference.',
        see: ['minimum-earned-premium', 'cancellation', 'direct-bill'],
    },
    {
        id: 'dividend',
        term: 'Dividend',
        category: 'money',
        def: 'A return of premium paid after the policy period by some mutual and comp carriers when results are good. Never guaranteed, and it cannot be promised in advance.',
        see: ['mutual-insurer', 'loss-ratio'],
    },
    // -------------------------------------------------------------- regulatory
    {
        id: 'doi',
        term: 'Department of Insurance',
        short: 'DOI',
        category: 'regulatory',
        def: 'The state regulator that licenses carriers and producers, approves rates and forms for admitted business, and handles consumer complaints.',
        see: ['admitted', 'filed-rates', 'naic', 'producer-license'],
    },
    {
        id: 'naic',
        term: 'NAIC',
        category: 'regulatory',
        def: 'The National Association of Insurance Commissioners — the coordinating body for state regulators. It writes model laws; the states decide whether to adopt them.',
        see: ['doi'],
    },
    {
        id: 'filed-rates',
        term: 'Filed rates and forms',
        category: 'regulatory',
        def: 'Admitted carriers must file their rates and policy wording with the state, and in many states get approval before use.',
        detail: 'It is why an admitted carrier will say a change is not possible when it plainly would be commercially sensible — they have no filed form for it.',
        see: ['admitted', 'non-filed', 'doi'],
    },
    {
        id: 'non-filed',
        term: 'Non-filed',
        category: 'regulatory',
        def: 'Lines exempt from rate and form filing — most inland marine, and all surplus lines. Carriers write their own wording.',
        detail: 'Flexibility with a cost: two inland marine forms for the same equipment can differ enormously, so comparing them is real work rather than a formality.',
        see: ['inland-marine', 'filed-rates', 'surplus-lines'],
    },
    {
        id: 'producer-license',
        term: 'Producer license',
        category: 'regulatory',
        essential: true,
        def: 'The state licence permitting someone to sell, solicit or negotiate insurance. Resident in your home state; non-resident in the others.',
        detail: 'It must be current in the state where the *risk* is located, not just where you sit. A surplus lines placement needs a separate surplus lines licence on top.',
        see: ['appointment', 'continuing-education', 'surplus-lines', 'doi'],
    },
    {
        id: 'appointment',
        term: 'Appointment',
        category: 'regulatory',
        def: 'A carrier formally authorising a licensed producer to represent it, filed with the state.',
        confused: 'A licence lets you sell insurance. An appointment lets you sell that carrier\'s insurance. You need both for admitted business.',
        see: ['producer-license', 'binding-authority'],
    },
    {
        id: 'continuing-education',
        term: 'Continuing education',
        short: 'CE',
        category: 'regulatory',
        def: 'Course hours required to renew a producer licence, usually including an ethics component, on a two-year cycle in most states.',
        see: ['producer-license'],
    },
    {
        id: 'apparent-authority',
        term: 'Apparent authority',
        category: 'regulatory',
        def: 'When a carrier\'s conduct leads an insured to reasonably believe an agent could act for it, the carrier can be bound even if the agent exceeded their actual authority.',
        detail: 'It protects the innocent insured, not the agent. The carrier pays the claim and then looks to the agency, which is where agents E&O comes in.',
        see: ['binding-authority', 'agents-eo'],
    },
    {
        id: 'agents-eo',
        term: 'Agents errors and omissions',
        category: 'regulatory',
        essential: true,
        def: 'Professional liability for the agency itself — claims that it failed to place coverage, placed the wrong coverage, or failed to advise.',
        detail: 'The recurring fact patterns are dull and avoidable: no flood, no employment practices, limits too low, a coverage the client declined with nothing in writing. Documenting declinations in the file is the cheapest E&O control there is.',
        see: ['professional-liability', 'declination-letter', 'apparent-authority'],
    },
    {
        id: 'declination-letter',
        term: 'Coverage declined letter',
        aka: ['rejection letter', 'coverage offered and declined'],
        category: 'practice',
        def: 'A written record that a coverage was offered to the insured and they chose not to buy it.',
        detail: 'The single most valuable document in an agency file. After a loss the conversation is always remembered differently; a signed or emailed decline is what settles it.',
        see: ['agents-eo', 'proposal'],
    },
];
